import axios from 'axios';

/**
 * Client of the Notifications V2 API (`docs/engine-contracts/notifications-v2-api.md`). The user
 * JWT travels in the axios default headers set by `utils/setAuthorizationToken`; the receipt-token
 * surface (`/notifications_v2/events`, no JWT) is used when there is no session.
 *
 * Nothing here logs a request body: bodies carry the push target.
 */
const base = () => `${process.env.REACT_APP_API_URL}/api/notifications_v2`;

/** The public receipt router hangs off the root, not off `/api`. */
const publicBase = () => `${process.env.REACT_APP_API_URL}/notifications_v2`;

const unwrap = res => res?.data?.data;

export const getConfig = async () =>
  unwrap(await axios.get(`${base()}/config`));

export const upsertEndpoint = async body =>
  unwrap(await axios.put(`${base()}/endpoints`, body));

export const updatePermission = async (installationId, body) =>
  unwrap(
    await axios.patch(
      `${base()}/endpoints/${encodeURIComponent(installationId)}/permission`,
      body,
    ),
  );

export const refreshTarget = async (installationId, target) =>
  unwrap(
    await axios.patch(
      `${base()}/endpoints/${encodeURIComponent(installationId)}/target`,
      { target },
    ),
  );

export const unbindEndpoint = async (installationId, headers = undefined) =>
  unwrap(
    await axios.post(
      `${base()}/endpoints/${encodeURIComponent(installationId)}/unbind`,
      {},
      headers ? { headers } : undefined,
    ),
  );

export const getPushStatus = async () =>
  unwrap(await axios.get(`${base()}/endpoints/status`));

export const postEvents = async events =>
  unwrap(await axios.post(`${base()}/events`, { events }));

export const postReceiptEvents = async events =>
  unwrap(await axios.post(`${publicBase()}/events`, { events }));

export const postSession = async session =>
  unwrap(await axios.post(`${base()}/sessions`, session));

export const getInbox = async (params = {}) =>
  unwrap(await axios.get(`${base()}/inbox`, { params }));

export const getUnreadCount = async () =>
  unwrap(await axios.get(`${base()}/inbox/unread-count`));

export const markRead = async notificationId =>
  unwrap(
    await axios.post(
      `${base()}/inbox/${encodeURIComponent(notificationId)}/read`,
    ),
  );

export const markAllRead = async () =>
  unwrap(await axios.post(`${base()}/inbox/read-all`));

export const archive = async notificationId =>
  unwrap(
    await axios.post(
      `${base()}/inbox/${encodeURIComponent(notificationId)}/archive`,
    ),
  );

/** A refusal the server explains (`409 { error }`), or null for any other failure. */
export const refusalOf = err => err?.response?.data?.error || null;
